// adjust the path if needed (same as in your component)
import { I18NextPipe } from 'angular-i18next';
import { AuditEntity } from '../../models';

// ----- MESSAGE ID GROUPS (ported from MVC) -----

const serviceVariableUpdateIds = new Set<string>([
  'Audit0070', 'Audit0055', 'Audit0057', 'Audit0062', 'Audit0063', 'Audit0064',
  'Audit0065', 'Audit0066', 'Audit0067', 'Audit0068', 'Audit0069', 'Audit0072',
  'Audit0073', 'Audit0074', 'Audit0075', 'Audit0076', 'Audit0077',
  'Audit0083', 'Audit0084', 'Audit0085', 'Audit0086', 'Audit0087', 'Audit0088',
  'Audit0089',
  'Audit0117', 'Audit0118', 'Audit0119', 'Audit0120', 'Audit0121', 'Audit0122',
  'Audit0123', 'Audit0124', 'Audit0125', 'Audit0126', 'Audit0127', 'Audit0128',
  'Audit0129', 'Audit0130', 'Audit0131',
  'Audit0106',
  'Audit0096', 'Audit0097', 'Audit0098', 'Audit0099', 'Audit0100', 'Audit0101',
  'Audit0143', 'Audit0144', 'Audit0145'
]);

const serviceAllocationIds = new Set<string>(['Audit0042', 'Audit0043']);

const twoVariableIds = new Set<string>([
  'Audit0114', 'Audit0115', 'Audit0116',
  'Audit0146', 'Audit0147',
  'Audit0172', 'Audit0173',
  'Audit0240', 'Audit0241', 'Audit0242', 'Audit0243'
]);

const threeVariableIds = new Set<string>([
  'Audit0052', 'Audit0053', 'Audit0030', 'Audit0028'
]);

const fromRangeIds = new Set<string>(['Audit0148', 'Audit0174']);

const varMiddleIds = new Set<string>([
  'Audit0039', 'Audit0040', 'Audit0041', 'Audit0113',
  'Audit0049', 'Audit0050', 'Audit0060', 'Audit0061',
  'Audit0078', 'Audit0079'
]);

const filterIds = new Set<string>(['Audit0046', 'Audit0047', 'Audit0048']);

const userStatusIds = new Set<string>(['Audit0051']);

const varMiddleMessageIdsSpecial = new Set<string>([
  'Audit0139', 'Audit0140', 'Audit0141', 'Audit0142'
]);

// KPI-related IDs (simplified handling)
const kpiRelatedIds = new Set<string>(['Audit0080', 'Audit0081']);

// IDs that use raw message (no extra formatting, just AntiXss+breakString in MVC)
const rawMessageIds = new Set<string>(['Audit0244', 'Audit0245']);

// ----- PUBLIC API -----

export function transformAuditResults(
  rawResults: AuditEntity[],
  i18n: I18NextPipe
): AuditEntity[] {
  return (rawResults || []).map(a => transformAuditEntity(a, i18n));
}

// ----- CORE TRANSFORMER -----

function transformAuditEntity(audit: AuditEntity, i18n: I18NextPipe): AuditEntity {
  const msgId = audit.messageId ?? '';
  let message = audit.message || '';

  // ------------------------
  // CASE 1: messageId is null (MVC ran default handler)
  // ------------------------
  if (!msgId) {
    message = sanitizeAndBreak(getServiceVariableUpdateString(message, '', i18n));
  }
  else {
    // ------------------------
    // CASE 2: messageId exists → use full branch logic
    // ------------------------
    if (rawMessageIds.has(msgId)) {
      message = sanitizeAndBreak(message);
    } else if (serviceAllocationIds.has(msgId)) {
      message = sanitizeAndBreak(getServiceAllocationString(message, msgId, i18n));
    } else if (twoVariableIds.has(msgId)) {
      message = sanitizeAndBreak(get2VariableMessageString(message, msgId, i18n));
    } else if (threeVariableIds.has(msgId)) {
      message = sanitizeAndBreak(get3VariableMessageString(message, msgId, i18n));
    } else if (fromRangeIds.has(msgId)) {
      message = sanitizeAndBreak(getFromRangeMessageString(message, msgId, i18n));
    } else if (varMiddleIds.has(msgId)) {
      message = sanitizeAndBreak(getVarMiddleMessageString(message, msgId, i18n));
    } else if (filterIds.has(msgId)) {
      message = sanitizeAndBreak(getFilterMessageString(message, i18n));
    } else if (userStatusIds.has(msgId)) {
      message = sanitizeAndBreak(getUserStatusMessageString(message, msgId, i18n));
    } else if (varMiddleMessageIdsSpecial.has(msgId)) {
      message = sanitizeAndBreak(getVarMiddleMessageString(message, msgId, i18n));
    } else if (kpiRelatedIds.has(msgId)) {
      message = sanitizeAndBreak(getServiceVariableUpdateString(message, msgId, i18n));
    } else if (msgId && !message.includes('§') && !message.includes('&#xA7;')) {
    // KEEP RAW MESSAGE EXACTLY AS BACKEND SENT IT
        message = sanitizeAndBreak(message);
    }
    else {
      // default branch EXACTLY like MVC
      message = sanitizeAndBreak(getServiceVariableUpdateString(message, msgId, i18n));
    }
  }

  // Translate KeyValue
  const translatedKeyValue = getLangValue(i18n, audit.keyValue || '');

  return {
    ...audit,
    message,
    keyValue: translatedKeyValue
  };
}


// ----- BASIC HELPERS -----

function getLangValue(i18n: I18NextPipe, key: string): string {
  if (!key) { return ''; }
  // same keys as before, but under "audit" namespace
  return i18n.transform('audit:lang_' + key, { defaultValue: key });
}

function format(template: string, ...args: (string | number)[]): string {
  if (!template) { return ''; }
  return template.replace(/{(\d+)}/g, (match, index) => {
    const i = Number(index);
    return args[i] !== undefined && args[i] !== null ? String(args[i]) : '';
  });
}

function htmlDecode(value: string): string {
  const txt = document.createElement('textarea');
  txt.innerHTML = value;
  return txt.value;
}

function splitMessage(message: string): string[] {
  const decoded = htmlDecode(message || '');
  return decoded.split('§');
}
// placeholder for AntiXss + breakString(ChangeTextLength)
function sanitizeAndBreak(text: string): string {
  // later you can mimic breakString or add extra sanitization here
  return text || '';
}

// ----- PORTED LOGIC (TS VERSIONS) -----

function getServiceVariableUpdateString(message: string, auditId: string, i18n: I18NextPipe): string {
  const base = getLangValue(i18n, auditId);
  const parts = splitMessage(message);

  if (parts.length > 1 && parts[1].trim() !== '') {
    const toText = i18n.transform('audit:To', { defaultValue: 'to' });
    const noValueText = i18n.transform('audit:audit_novalue', { defaultValue: 'no value' });

    const valuePart = parts[1].trim() === '-1' ? noValueText : parts[1].trim();
    return `${base} ${toText} ${valuePart}`;
  }

  return base;
}

function getDiallerParamUpdateString(message: string, auditId: string, i18n: I18NextPipe): string {
  const base = getLangValue(i18n, auditId);
  const parts = splitMessage(message);

  if (parts.length > 1 && parts[1].trim() !== '') {
    const noValueText = i18n.transform('audit:audit_novalue', { defaultValue: 'no value' });
    const valuePart = parts[1].trim() === '-1' ? noValueText : parts[1].trim();
    return `${base} ${valuePart}`;
  }

  return base;
}

function get2VariableMessageString(message: string, auditId: string, i18n: I18NextPipe): string {
  const parts = splitMessage(message);
  const template = getLangValue(i18n, auditId);

  if (parts.length > 1 && parts[0].trim() && parts[1].trim()) {
    return format(template, parts[0].trim(), parts[1].trim());
  }

  return format(template, '', '');
}

function get3VariableMessageString(message: string, auditId: string, i18n: I18NextPipe): string {
  const parts = splitMessage(message);
  const template = getLangValue(i18n, auditId);
  const fromText = i18n.transform('audit:From', { defaultValue: 'from' });
  const toText = i18n.transform('audit:To', { defaultValue: 'to' });
  const noValueText = i18n.transform('audit:audit_novalue', { defaultValue: 'no value' });

  const safe = (v: string | undefined) =>
    !v || v.trim() === '-1' ? noValueText : v.trim();

  let result: string;

  if (parts.length > 2 && parts[0].trim() && parts[1].trim() && parts[2].trim()) {
    result =
      format(template, parts[0].trim()) +
      ` ${fromText} ${safe(parts[2])}` +
      ` ${toText} ${safe(parts[1])}`;
  } else if (parts.length > 1 && parts[0].trim() && parts[1].trim()) {
    result =
      format(template, parts[0].trim()) +
      ` ${toText} ${safe(parts[1])}`;
  } else if (parts.length > 0 && parts[0].trim()) {
    result = format(template, parts[0].trim());
  } else {
    result = format(template, '');
  }

  return result;
}

function getFromRangeMessageString(message: string, auditId: string, i18n: I18NextPipe): string {
  const parts = splitMessage(message);
  const template = getLangValue(i18n, auditId);

  if (parts.length > 1 && parts[0].trim() && parts[1].trim()) {
    const last = parts.length > 2 ? parts[2] : parts[1];
    return format(template, parts[1].trim(), last.trim());
  }

  return format(template, '', '');
}

function getServiceAllocationString(message: string, auditId: string, i18n: I18NextPipe): string {
  const parts = splitMessage(message);
  if (parts.length <= 1 || !parts[1].trim()) {
    return '';
  }

  const val = parts[1].trim();

  if (auditId === 'Audit0042') {
    if (val === '1') {
      return i18n.transform('audit:Allocation_type_1_1', { defaultValue: 'Allocation type 1 = 1' });
    }
    if (val === '0') {
      return i18n.transform('audit:Allocation_type_1_0', { defaultValue: 'Allocation type 1 = 0' });
    }
  } else if (auditId === 'Audit0043') {
    if (val === '1') {
      return i18n.transform('audit:Allocation_type_2_1', { defaultValue: 'Allocation type 2 = 1' });
    }
    if (val === '0') {
      return i18n.transform('audit:Allocation_type_2_0', { defaultValue: 'Allocation type 2 = 0' });
    }
  }

  return '';
}

function getVarMiddleMessageString(message: string, auditIdOrMsgId: string, i18n: I18NextPipe): string {
  const parts = splitMessage(message);
  const template = getLangValue(i18n, auditIdOrMsgId);

  if (parts.length > 1 && parts[1].trim()) {
    return format(template, parts[1].trim());
  }

  return format(template, '');
}

function getFilterMessageString(message: string, i18n: I18NextPipe): string {
  const parts = splitMessage(message);
  if (parts.length <= 1 || !parts[1].trim()) {
    return '';
  }

  const msg = parts[1].trim();
  const updatedText = i18n.transform('audit:updated', { defaultValue: 'updated' });
  const activatedText = i18n.transform('audit:activated', { defaultValue: 'activated' });
  const deactivatedText = i18n.transform('audit:deactivated', { defaultValue: 'deactivated' });
  const forText = i18n.transform('audit:For', { defaultValue: 'for' });
  const dashHtml = '–';

  if (msg.includes(':')) {
    const ctxArr = msg.split(':');
    if (ctxArr.length > 1 && ctxArr[1].trim()) {
      const contextId = ctxArr[0].trim();
      const filterId = ctxArr[1].trim();

      const filterName = getFilterName(filterId, i18n);
      const contextName = getContextName(contextId, i18n);

      if (message.includes('Filter context activated')) {
        return `${filterName} ${dashHtml} ${activatedText} ${forText} ${contextName}`;
      }

      if (message.includes('Filter context deactivated')) {
        return `${filterName} ${dashHtml} ${deactivatedText} ${forText} ${contextName}`;
      }
    }
  }

  const filterNameOnly = getFilterName(msg, i18n);
  return `${filterNameOnly} ${updatedText}`;
}

function getUserStatusMessageString(message: string, auditId: string, i18n: I18NextPipe): string {
  const base = getLangValue(i18n, auditId);
  const parts = splitMessage(message);
  const toText = i18n.transform('audit:To', { defaultValue: 'to' });

  if (parts.length > 1 && parts[1].trim()) {
    const status = getUserStatus(parts[1].trim(), i18n);
    return `${base} ${toText} ${status}`;
  }

  return base;
}

// ----- FILTER / CONTEXT / STATUS HELPERS -----

function getFilterName(filterId: string, i18n: I18NextPipe): string {
  const key = {
    '1': 'usersfilter_ResourceType_1',
    '2': 'usersfilter_ResourceType_2',
    '3': 'usersfilter_ResourceType_3',
    '4': 'usersfilter_ResourceType_4',
    '5': 'usersfilter_ResourceType_5',
    '6': 'usersfilter_ResourceType_6',
    '7': 'usersfilter_ResourceType_7',
    '8': 'usersfilter_ResourceType_8',
    '9': 'usersfilter_ResourceType_9',
    '10': 'usersfilter_ResourceType_10',
    '11': 'usersfilter_ResourceType_11',
    '12': 'usersfilter_ResourceType_12',
    '13': 'usersfilter_ResourceType_13',
    '14': 'usersfilter_ResourceType_14',
    '15': 'usersfilter_ResourceType_15',
    '16': 'usersfilter_ResourceType_16',
    '17': 'usersfilter_ResourceType_17',
    '18': 'usersfilter_ResourceType_18',
    '19': 'usersfilter_ResourceType_19'
  }[filterId];

  if (!key) { return ''; }

  // same key, but in audit namespace as you requested
  return i18n.transform('users:' + key, { defaultValue: key });
}

function getContextName(contextId: string, i18n: I18NextPipe): string {
  let key: string | undefined;

  switch (contextId) {
    case '1':
      key = 'context_control';
      break;
    case '2':
      key = 'context_statistics';
      break;
    case '4':
      key = 'usersfilter_ResourceType_9';
      break;
  }

  if (!key) { return ''; }
  return i18n.transform('audit:' + key, { defaultValue: key });
}

function getUserStatus(statusCode: string, i18n: I18NextPipe): string {
  // e.g. key: "userstatus_<code>" in audit namespace
  return i18n.transform('audit:userstatus_' + statusCode, { defaultValue: statusCode });
}
